// Central UI + section content translations for EN / FR / DE.
// Long-form page content lives in /src/data. This holds navigation,
// homepage sections, footer, forms and shared UI strings.

export const translations = {
  en: {
    dir: "ltr",
    nav: {
      treatments: "Treatments",
      implants: "Dental Implants",
      prices: "Prices",
      clinics: "Clinics",
      locations: "Locations",
      beforeAfter: "Before & After",
      patientGuide: "Patient Guide",
      blog: "Blog",
      faq: "FAQ",
      contact: "Contact",
    },
    cta: {
      freePlan: "Get Free Treatment Plan",
      freePlanShort: "Free Treatment Plan",
      myFreePlan: "Get My Free Treatment Plan",
      whatsapp: "WhatsApp Us",
      menu: "Menu",
      close: "Close",
      readGuide: "Read the guide",
      viewTreatment: "View treatment",
      exploreCity: "Explore city",
      allTreatments: "View all treatments",
      learnMore: "Learn more",
    },
    hero: {
      eyebrow: "International Dental Patient Support",
      h1a: "Dental Treatment",
      h1b: "in Vietnam",
      h1c: "Made Simple",
      sub: "Explore dental treatment options in Vietnam with clear information, selected providers and support for international patients — from consultation through aftercare.",
      trust: [
        "Selected Licensed Providers",
        "Clear Treatment Information",
        "International Patient Support",
        "English • Français • Deutsch",
      ],
      microA: "Send your dental photos or X-rays",
      microB: "Initial case review",
      microC: "No obligation",
    },
    marquee: [
      "Independent Patient Platform",
      "Clear Treatment Information",
      "Selected Licensed Providers",
      "International Patient Support",
      "Consultation to Aftercare",
      "English · Français · Deutsch",
    ],
    treatmentsSection: {
      eyebrow: "Popular Treatments",
      title: "Explore common dental treatments",
      sub: "Clear, independent information on the treatments international patients most often ask about. Each guide links to a dedicated page.",
    },
    whyVietnam: {
      eyebrow: "Why Consider Vietnam",
      title: "Why consider dental treatment in Vietnam?",
      sub: "Vietnam has become a destination many international patients explore for dental care. Standards vary by provider — here is what selected clinics may offer.",
      note: "Treatment options and technology vary by provider. Selected providers may offer some or all of the below.",
      chapters: [
        { t: "Modern dental facilities", d: "Depending on the clinic, selected providers operate modern, purpose-built dental facilities designed for international patients." },
        { t: "Experienced professionals", d: "Selected providers may have dentists with significant experience in implantology, prosthetics and cosmetic dentistry." },
        { t: "Advanced technology", d: "Some clinics use digital imaging, CBCT scanning and CAD/CAM workflows. Technology varies by provider." },
        { t: "International implant systems", d: "Selected providers may offer internationally recognised implant systems. Confirm brands directly with the clinic." },
        { t: "Competitive treatment costs", d: "Treatment costs in Vietnam may be lower than in some countries, though final costs depend on your individual case." },
        { t: "A destination to combine with travel", d: "Vietnam is a popular travel destination, giving patients the option to combine treatment with recovery time." },
      ],
    },
    howItWorks: {
      eyebrow: "How It Works",
      title: "A simple path from question to aftercare",
      sub: "A clear, no-obligation process designed for international patients.",
      steps: [
        { t: "Tell Us What You Need", d: "Share the treatment you are considering and your questions." },
        { t: "Send Photos / X-rays", d: "Send dental photos or X-rays for an initial, informal review." },
        { t: "Case Reviewed", d: "Your information is reviewed with selected licensed providers." },
        { t: "Receive Treatment Options", d: "Get clear treatment options and indicative information." },
        { t: "Plan Your Vietnam Visit", d: "We help you understand timing, travel and what to expect." },
        { t: "Treatment & Aftercare", d: "Attend your treatment with support and aftercare guidance." },
      ],
    },
    locationsSection: {
      eyebrow: "Top Locations",
      title: "Popular destinations for dental care",
      sub: "Three cities international patients most often consider. Each has its own guide.",
    },
    trust: {
      eyebrow: "Choose With Confidence",
      title: "Choosing dental care in Vietnam with confidence",
      sub: "Before choosing any clinic — in Vietnam or elsewhere — we encourage patients to check the following.",
      checklist: [
        "Clinic licence",
        "Dentist credentials",
        "Treatment experience",
        "Implant / material brands",
        "Sterilisation procedures",
        "Diagnostic technology",
        "Treatment plan",
        "Costs",
        "Warranty terms",
        "Aftercare",
      ],
      cta: "How to Choose a Dental Clinic in Vietnam",
    },
    reviews: {
      eyebrow: "Patient Voices",
      title: "Verified patient reviews",
      empty: "We are collecting verified patient reviews. To protect trust and integrity, we do not publish reviews until they are verified. Check back soon.",
    },
    faqSection: {
      eyebrow: "FAQ",
      title: "Frequently asked questions",
      sub: "Straightforward answers to the questions international patients ask most.",
      viewAll: "View all FAQs",
    },
    blogSection: {
      eyebrow: "Dental Guides",
      title: "In-depth dental guides",
      sub: "Independent, carefully written guides for international patients considering treatment in Vietnam.",
      viewAll: "View all guides",
    },
    ctaBand: {
      title: "Ready to understand your options?",
      sub: "Send your dental photos or X-rays for an initial, no-obligation case review.",
    },
    footer: {
      about: "Dentel Vietnam is an independent dental information and patient-support platform helping international patients understand dental treatment in Vietnam and connect with selected licensed providers. We are not a dental clinic and do not provide dental treatment.",
      treatments: "Treatments",
      locations: "Locations",
      company: "Company",
      legal: "Trust & Legal",
      contact: "Contact",
      rights: "All rights reserved.",
      disclaimerShort: "Dentel Vietnam does not diagnose patients or provide dental treatment. All clinical decisions are made by licensed dental professionals.",
      links: {
        about: "About Dentel Vietnam",
        howWeSelect: "How We Select Providers",
        editorial: "Editorial Policy",
        medicalReview: "Medical Review Policy",
        disclaimer: "Treatment Information Disclaimer",
        privacy: "Privacy Policy",
        terms: "Terms",
        contact: "Contact",
        patientGuide: "Patient Guide",
        prices: "Prices",
        clinics: "Clinics",
        blog: "Dental Guides",
        faq: "FAQ",
      },
    },
    form: {
      title: "Get your free treatment plan",
      sub: "Send a few details and, if you like, your dental photos or X-rays. Initial case review, no obligation.",
      fullName: "Full Name",
      country: "Country",
      phone: "WhatsApp / Phone",
      email: "Email",
      treatment: "Treatment Interested In",
      city: "Preferred City",
      travelDate: "Approximate Travel Date",
      message: "Message",
      upload: "Optional dental photo / X-ray upload",
      uploadHint: "JPG, PNG or PDF — max 10MB",
      consent: "I agree to the Privacy Policy and consent to being contacted about my enquiry.",
      submit: "Send My Enquiry",
      submitting: "Sending…",
      selectTreatment: "Select a treatment",
      selectCity: "Select a city",
      noPreference: "No preference",
      successTitle: "Thank you — your enquiry has been received",
      successBody: "We will review your details and reply. This is an initial, no-obligation review and not a diagnosis or treatment recommendation.",
      errorRequired: "This field is required",
      errorEmail: "Please enter a valid email address",
      errorConsent: "Please accept the privacy policy to continue",
      errorGeneric: "Something went wrong. Please try again or contact us on WhatsApp.",
    },
    common: {
      home: "Home",
      relatedTreatments: "Related treatments",
      relatedCities: "Related cities",
      relatedArticles: "Related guides",
      references: "References",
      tableOfContents: "On this page",
      keyTakeaways: "Key takeaways",
      author: "Author",
      medicalReviewer: "Medically reviewed by",
      published: "Published",
      updated: "Updated",
      readTime: "min read",
      disclaimerTitle: "Medical information disclaimer",
      disclaimer: "This content is for general information only and is not medical advice, diagnosis or a treatment recommendation. All diagnosis, suitability and clinical decisions must be made by a licensed dental professional after an in-person assessment.",
      indicativePrice: "Indicative price only. Final treatment costs depend on clinical assessment, diagnostics, materials and treatment complexity.",
      pricePlaceholder: "Price available after case review",
      notFoundTitle: "Page not found",
      notFoundBody: "The page you are looking for doesn’t exist or has moved.",
      backHome: "Back to homepage",
      whatToCheck: "What to check before you choose",
    },
  },

  fr: {
    dir: "ltr",
    nav: {
      treatments: "Traitements",
      implants: "Implants dentaires",
      prices: "Tarifs",
      clinics: "Cliniques",
      locations: "Destinations",
      beforeAfter: "Avant / Après",
      patientGuide: "Guide du patient",
      blog: "Blog",
      faq: "FAQ",
      contact: "Contact",
    },
    cta: {
      freePlan: "Plan de traitement gratuit",
      freePlanShort: "Plan gratuit",
      myFreePlan: "Obtenir mon plan gratuit",
      whatsapp: "Nous contacter (WhatsApp)",
      menu: "Menu",
      close: "Fermer",
      readGuide: "Lire le guide",
      viewTreatment: "Voir le traitement",
      exploreCity: "Découvrir la ville",
      allTreatments: "Voir tous les traitements",
      learnMore: "En savoir plus",
    },
    hero: {
      eyebrow: "Accompagnement des patients internationaux",
      h1a: "Les soins dentaires",
      h1b: "au Vietnam",
      h1c: "en toute simplicité",
      sub: "Explorez les options de soins dentaires au Vietnam avec des informations claires, des prestataires sélectionnés et un accompagnement des patients internationaux — de la consultation au suivi post-traitement.",
      trust: [
        "Prestataires agréés sélectionnés",
        "Informations claires sur les traitements",
        "Accompagnement des patients internationaux",
        "English • Français • Deutsch",
      ],
      microA: "Envoyez vos photos ou radiographies dentaires",
      microB: "Première évaluation du dossier",
      microC: "Sans engagement",
    },
    marquee: [
      "Plateforme indépendante",
      "Informations claires",
      "Prestataires agréés sélectionnés",
      "Accompagnement international",
      "De la consultation au suivi",
      "English · Français · Deutsch",
    ],
    treatmentsSection: {
      eyebrow: "Traitements populaires",
      title: "Découvrez les traitements dentaires courants",
      sub: "Des informations claires et indépendantes sur les traitements les plus demandés par les patients internationaux. Chaque guide renvoie à une page dédiée.",
    },
    whyVietnam: {
      eyebrow: "Pourquoi le Vietnam",
      title: "Pourquoi envisager des soins dentaires au Vietnam ?",
      sub: "Le Vietnam est devenu une destination explorée par de nombreux patients internationaux. Les standards varient selon le prestataire — voici ce que certaines cliniques sélectionnées peuvent proposer.",
      note: "Les options de traitement et les technologies varient selon le prestataire. Les prestataires sélectionnés peuvent proposer tout ou partie des éléments ci-dessous.",
      chapters: [
        { t: "Installations dentaires modernes", d: "Selon la clinique, certains prestataires sélectionnés disposent d’installations dentaires modernes conçues pour les patients internationaux." },
        { t: "Professionnels expérimentés", d: "Certains prestataires sélectionnés emploient des dentistes expérimentés en implantologie, prothèse et esthétique dentaire." },
        { t: "Technologies avancées", d: "Certaines cliniques utilisent l’imagerie numérique, le scanner CBCT et les flux CAD/CAM. La technologie varie selon le prestataire." },
        { t: "Systèmes d’implants internationaux", d: "Certains prestataires peuvent proposer des systèmes d’implants reconnus. Confirmez les marques directement auprès de la clinique." },
        { t: "Coûts de traitement compétitifs", d: "Les coûts au Vietnam peuvent être inférieurs à ceux de certains pays, mais le coût final dépend de votre cas." },
        { t: "Une destination à combiner au voyage", d: "Le Vietnam est une destination touristique prisée, permettant d’associer traitement et temps de récupération." },
      ],
    },
    howItWorks: {
      eyebrow: "Comment ça marche",
      title: "Un parcours simple, de la question au suivi",
      sub: "Un processus clair et sans engagement, pensé pour les patients internationaux.",
      steps: [
        { t: "Dites-nous vos besoins", d: "Indiquez le traitement envisagé et vos questions." },
        { t: "Envoyez photos / radios", d: "Envoyez vos photos ou radiographies pour une première évaluation informelle." },
        { t: "Dossier évalué", d: "Vos informations sont examinées avec des prestataires agréés sélectionnés." },
        { t: "Recevez des options", d: "Obtenez des options de traitement claires et des informations indicatives." },
        { t: "Planifiez votre séjour", d: "Nous vous aidons à comprendre le calendrier, le voyage et les étapes." },
        { t: "Traitement et suivi", d: "Réalisez votre traitement avec accompagnement et conseils de suivi." },
      ],
    },
    locationsSection: {
      eyebrow: "Principales destinations",
      title: "Destinations prisées pour les soins dentaires",
      sub: "Les trois villes les plus souvent envisagées par les patients internationaux. Chacune a son guide.",
    },
    trust: {
      eyebrow: "Choisir en confiance",
      title: "Choisir des soins dentaires au Vietnam en toute confiance",
      sub: "Avant de choisir une clinique — au Vietnam ou ailleurs — nous encourageons les patients à vérifier les points suivants.",
      checklist: [
        "Licence de la clinique",
        "Diplômes du dentiste",
        "Expérience du traitement",
        "Marques d’implants / matériaux",
        "Procédures de stérilisation",
        "Technologie de diagnostic",
        "Plan de traitement",
        "Coûts",
        "Conditions de garantie",
        "Suivi post-traitement",
      ],
      cta: "Comment choisir une clinique dentaire au Vietnam",
    },
    reviews: {
      eyebrow: "Paroles de patients",
      title: "Avis vérifiés de patients",
      empty: "Nous recueillons actuellement des avis vérifiés de patients. Par souci d’intégrité, nous ne publions pas d’avis avant leur vérification. Revenez bientôt.",
    },
    faqSection: {
      eyebrow: "FAQ",
      title: "Questions fréquentes",
      sub: "Des réponses claires aux questions les plus posées par les patients internationaux.",
      viewAll: "Voir toutes les FAQ",
    },
    blogSection: {
      eyebrow: "Guides dentaires",
      title: "Guides dentaires détaillés",
      sub: "Des guides indépendants et soignés pour les patients internationaux envisageant un traitement au Vietnam.",
      viewAll: "Voir tous les guides",
    },
    ctaBand: {
      title: "Prêt à comprendre vos options ?",
      sub: "Envoyez vos photos ou radiographies pour une première évaluation sans engagement.",
    },
    footer: {
      about: "Dentel Vietnam est une plateforme indépendante d’information et d’accompagnement des patients qui aide les patients internationaux à comprendre les soins dentaires au Vietnam et à contacter des prestataires agréés sélectionnés. Nous ne sommes pas une clinique dentaire et ne fournissons pas de soins dentaires.",
      treatments: "Traitements",
      locations: "Destinations",
      company: "Société",
      legal: "Confiance & mentions légales",
      contact: "Contact",
      rights: "Tous droits réservés.",
      disclaimerShort: "Dentel Vietnam n’établit pas de diagnostic et ne fournit pas de soins dentaires. Toutes les décisions cliniques sont prises par des professionnels dentaires agréés.",
      links: {
        about: "À propos de Dentel Vietnam",
        howWeSelect: "Comment nous sélectionnons les prestataires",
        editorial: "Politique éditoriale",
        medicalReview: "Politique de révision médicale",
        disclaimer: "Avertissement sur les informations de traitement",
        privacy: "Politique de confidentialité",
        terms: "Conditions",
        contact: "Contact",
        patientGuide: "Guide du patient",
        prices: "Tarifs",
        clinics: "Cliniques",
        blog: "Guides dentaires",
        faq: "FAQ",
      },
    },
    form: {
      title: "Obtenez votre plan de traitement gratuit",
      sub: "Transmettez quelques informations et, si vous le souhaitez, vos photos ou radiographies. Première évaluation sans engagement.",
      fullName: "Nom complet",
      country: "Pays",
      phone: "WhatsApp / Téléphone",
      email: "E-mail",
      treatment: "Traitement souhaité",
      city: "Ville préférée",
      travelDate: "Date de voyage approximative",
      message: "Message",
      upload: "Photo / radiographie dentaire (optionnel)",
      uploadHint: "JPG, PNG ou PDF — max 10 Mo",
      consent: "J’accepte la politique de confidentialité et consens à être contacté au sujet de ma demande.",
      submit: "Envoyer ma demande",
      submitting: "Envoi…",
      selectTreatment: "Choisir un traitement",
      selectCity: "Choisir une ville",
      noPreference: "Sans préférence",
      successTitle: "Merci — votre demande a bien été reçue",
      successBody: "Nous examinerons vos informations et vous répondrons. Il s’agit d’une première évaluation sans engagement, et non d’un diagnostic ou d’une recommandation de traitement.",
      errorRequired: "Ce champ est obligatoire",
      errorEmail: "Veuillez saisir une adresse e-mail valide",
      errorConsent: "Veuillez accepter la politique de confidentialité pour continuer",
      errorGeneric: "Une erreur est survenue. Réessayez ou contactez-nous sur WhatsApp.",
    },
    common: {
      home: "Accueil",
      relatedTreatments: "Traitements associés",
      relatedCities: "Villes associées",
      relatedArticles: "Guides associés",
      references: "Références",
      tableOfContents: "Sur cette page",
      keyTakeaways: "À retenir",
      author: "Auteur",
      medicalReviewer: "Révisé médicalement par",
      published: "Publié le",
      updated: "Mis à jour le",
      readTime: "min de lecture",
      disclaimerTitle: "Avertissement d’information médicale",
      disclaimer: "Ce contenu est fourni à titre d’information générale uniquement et ne constitue pas un avis médical, un diagnostic ni une recommandation de traitement. Tout diagnostic et toute décision clinique doivent être établis par un professionnel dentaire agréé après un examen en personne.",
      indicativePrice: "Prix indicatif uniquement. Le coût final dépend de l’évaluation clinique, des diagnostics, des matériaux et de la complexité du traitement.",
      pricePlaceholder: "Prix disponible après évaluation du dossier",
      notFoundTitle: "Page introuvable",
      notFoundBody: "La page que vous recherchez n’existe pas ou a été déplacée.",
      backHome: "Retour à l’accueil",
      whatToCheck: "À vérifier avant de choisir",
    },
  },

  de: {
    dir: "ltr",
    nav: {
      treatments: "Behandlungen",
      implants: "Zahnimplantate",
      prices: "Preise",
      clinics: "Kliniken",
      locations: "Standorte",
      beforeAfter: "Vorher / Nachher",
      patientGuide: "Patientenratgeber",
      blog: "Blog",
      faq: "FAQ",
      contact: "Kontakt",
    },
    cta: {
      freePlan: "Kostenloser Behandlungsplan",
      freePlanShort: "Kostenloser Plan",
      myFreePlan: "Meinen kostenlosen Plan erhalten",
      whatsapp: "WhatsApp schreiben",
      menu: "Menü",
      close: "Schließen",
      readGuide: "Ratgeber lesen",
      viewTreatment: "Behandlung ansehen",
      exploreCity: "Stadt entdecken",
      allTreatments: "Alle Behandlungen ansehen",
      learnMore: "Mehr erfahren",
    },
    hero: {
      eyebrow: "Betreuung internationaler Patienten",
      h1a: "Zahnbehandlung",
      h1b: "in Vietnam",
      h1c: "ganz einfach",
      sub: "Entdecken Sie Optionen für Zahnbehandlungen in Vietnam mit klaren Informationen, ausgewählten Anbietern und Unterstützung für internationale Patienten — von der Beratung bis zur Nachsorge.",
      trust: [
        "Ausgewählte lizenzierte Anbieter",
        "Klare Behandlungsinformationen",
        "Betreuung internationaler Patienten",
        "English • Français • Deutsch",
      ],
      microA: "Senden Sie Ihre Zahnfotos oder Röntgenbilder",
      microB: "Erste Fallprüfung",
      microC: "Unverbindlich",
    },
    marquee: [
      "Unabhängige Plattform",
      "Klare Informationen",
      "Ausgewählte lizenzierte Anbieter",
      "Internationale Betreuung",
      "Von der Beratung bis zur Nachsorge",
      "English · Français · Deutsch",
    ],
    treatmentsSection: {
      eyebrow: "Beliebte Behandlungen",
      title: "Entdecken Sie gängige Zahnbehandlungen",
      sub: "Klare, unabhängige Informationen zu den Behandlungen, nach denen internationale Patienten am häufigsten fragen. Jeder Ratgeber führt zu einer eigenen Seite.",
    },
    whyVietnam: {
      eyebrow: "Warum Vietnam",
      title: "Warum eine Zahnbehandlung in Vietnam in Betracht ziehen?",
      sub: "Vietnam ist für viele internationale Patienten zu einem interessanten Ziel für Zahnbehandlungen geworden. Die Standards variieren je nach Anbieter — dies können ausgewählte Kliniken bieten.",
      note: "Behandlungsoptionen und Technologien variieren je nach Anbieter. Ausgewählte Anbieter bieten möglicherweise einige oder alle der folgenden Punkte.",
      chapters: [
        { t: "Moderne Zahnkliniken", d: "Je nach Klinik verfügen ausgewählte Anbieter über moderne, speziell gebaute Praxen für internationale Patienten." },
        { t: "Erfahrene Fachkräfte", d: "Ausgewählte Anbieter beschäftigen möglicherweise Zahnärzte mit umfangreicher Erfahrung in Implantologie, Prothetik und Ästhetik." },
        { t: "Fortschrittliche Technologie", d: "Einige Kliniken nutzen digitale Bildgebung, CBCT-Scans und CAD/CAM-Verfahren. Die Technik variiert je nach Anbieter." },
        { t: "Internationale Implantatsysteme", d: "Ausgewählte Anbieter bieten möglicherweise anerkannte Implantatsysteme. Bestätigen Sie die Marken direkt bei der Klinik." },
        { t: "Wettbewerbsfähige Kosten", d: "Die Kosten in Vietnam können niedriger sein als in einigen Ländern, doch die Endkosten hängen von Ihrem Fall ab." },
        { t: "Mit einer Reise kombinierbar", d: "Vietnam ist ein beliebtes Reiseziel, sodass sich Behandlung und Erholung verbinden lassen." },
      ],
    },
    howItWorks: {
      eyebrow: "So funktioniert es",
      title: "Ein einfacher Weg von der Frage bis zur Nachsorge",
      sub: "Ein klarer, unverbindlicher Ablauf für internationale Patienten.",
      steps: [
        { t: "Sagen Sie uns Ihren Bedarf", d: "Teilen Sie die gewünschte Behandlung und Ihre Fragen mit." },
        { t: "Fotos / Röntgen senden", d: "Senden Sie Zahnfotos oder Röntgenbilder für eine erste, informelle Prüfung." },
        { t: "Fall geprüft", d: "Ihre Angaben werden mit ausgewählten lizenzierten Anbietern geprüft." },
        { t: "Behandlungsoptionen erhalten", d: "Erhalten Sie klare Behandlungsoptionen und Richtinformationen." },
        { t: "Vietnam-Reise planen", d: "Wir helfen Ihnen, Zeitplan, Reise und Ablauf zu verstehen." },
        { t: "Behandlung & Nachsorge", d: "Nehmen Sie Ihre Behandlung mit Betreuung und Nachsorge wahr." },
      ],
    },
    locationsSection: {
      eyebrow: "Top-Standorte",
      title: "Beliebte Ziele für Zahnbehandlungen",
      sub: "Die drei Städte, die internationale Patienten am häufigsten in Betracht ziehen. Jede hat einen eigenen Ratgeber.",
    },
    trust: {
      eyebrow: "Mit Vertrauen wählen",
      title: "Zahnbehandlung in Vietnam mit Vertrauen wählen",
      sub: "Bevor Sie eine Klinik wählen — in Vietnam oder anderswo — empfehlen wir Patienten, Folgendes zu prüfen.",
      checklist: [
        "Kliniklizenz",
        "Qualifikationen des Zahnarztes",
        "Behandlungserfahrung",
        "Implantat-/Materialmarken",
        "Sterilisationsverfahren",
        "Diagnosetechnik",
        "Behandlungsplan",
        "Kosten",
        "Garantiebedingungen",
        "Nachsorge",
      ],
      cta: "Wie man eine Zahnklinik in Vietnam auswählt",
    },
    reviews: {
      eyebrow: "Patientenstimmen",
      title: "Verifizierte Patientenbewertungen",
      empty: "Wir sammeln derzeit verifizierte Patientenbewertungen. Aus Gründen der Integrität veröffentlichen wir Bewertungen erst nach ihrer Verifizierung. Schauen Sie bald wieder vorbei.",
    },
    faqSection: {
      eyebrow: "FAQ",
      title: "Häufig gestellte Fragen",
      sub: "Klare Antworten auf die häufigsten Fragen internationaler Patienten.",
      viewAll: "Alle FAQ ansehen",
    },
    blogSection: {
      eyebrow: "Zahn-Ratgeber",
      title: "Ausführliche Zahn-Ratgeber",
      sub: "Unabhängige, sorgfältig verfasste Ratgeber für internationale Patienten, die eine Behandlung in Vietnam erwägen.",
      viewAll: "Alle Ratgeber ansehen",
    },
    ctaBand: {
      title: "Bereit, Ihre Optionen zu verstehen?",
      sub: "Senden Sie Ihre Zahnfotos oder Röntgenbilder für eine erste, unverbindliche Fallprüfung.",
    },
    footer: {
      about: "Dentel Vietnam ist eine unabhängige Informations- und Patientenplattform, die internationalen Patienten hilft, Zahnbehandlungen in Vietnam zu verstehen und ausgewählte lizenzierte Anbieter zu kontaktieren. Wir sind keine Zahnklinik und bieten keine Zahnbehandlungen an.",
      treatments: "Behandlungen",
      locations: "Standorte",
      company: "Unternehmen",
      legal: "Vertrauen & Rechtliches",
      contact: "Kontakt",
      rights: "Alle Rechte vorbehalten.",
      disclaimerShort: "Dentel Vietnam stellt keine Diagnosen und bietet keine Zahnbehandlungen an. Alle klinischen Entscheidungen treffen lizenzierte Zahnärzte.",
      links: {
        about: "Über Dentel Vietnam",
        howWeSelect: "Wie wir Anbieter auswählen",
        editorial: "Redaktionsrichtlinie",
        medicalReview: "Richtlinie zur medizinischen Prüfung",
        disclaimer: "Hinweis zu Behandlungsinformationen",
        privacy: "Datenschutzerklärung",
        terms: "AGB",
        contact: "Kontakt",
        patientGuide: "Patientenratgeber",
        prices: "Preise",
        clinics: "Kliniken",
        blog: "Zahn-Ratgeber",
        faq: "FAQ",
      },
    },
    form: {
      title: "Erhalten Sie Ihren kostenlosen Behandlungsplan",
      sub: "Senden Sie einige Angaben und, wenn Sie möchten, Ihre Zahnfotos oder Röntgenbilder. Erste Fallprüfung, unverbindlich.",
      fullName: "Vollständiger Name",
      country: "Land",
      phone: "WhatsApp / Telefon",
      email: "E-Mail",
      treatment: "Gewünschte Behandlung",
      city: "Bevorzugte Stadt",
      travelDate: "Ungefähres Reisedatum",
      message: "Nachricht",
      upload: "Zahnfoto / Röntgenbild (optional)",
      uploadHint: "JPG, PNG oder PDF — max. 10 MB",
      consent: "Ich akzeptiere die Datenschutzerklärung und stimme zu, zu meiner Anfrage kontaktiert zu werden.",
      submit: "Anfrage senden",
      submitting: "Senden…",
      selectTreatment: "Behandlung wählen",
      selectCity: "Stadt wählen",
      noPreference: "Keine Präferenz",
      successTitle: "Danke — Ihre Anfrage ist eingegangen",
      successBody: "Wir prüfen Ihre Angaben und melden uns. Dies ist eine erste, unverbindliche Prüfung und keine Diagnose oder Behandlungsempfehlung.",
      errorRequired: "Dieses Feld ist erforderlich",
      errorEmail: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
      errorConsent: "Bitte akzeptieren Sie die Datenschutzerklärung, um fortzufahren",
      errorGeneric: "Etwas ist schiefgelaufen. Bitte erneut versuchen oder per WhatsApp kontaktieren.",
    },
    common: {
      home: "Startseite",
      relatedTreatments: "Ähnliche Behandlungen",
      relatedCities: "Ähnliche Städte",
      relatedArticles: "Ähnliche Ratgeber",
      references: "Quellen",
      tableOfContents: "Auf dieser Seite",
      keyTakeaways: "Das Wichtigste",
      author: "Autor",
      medicalReviewer: "Medizinisch geprüft von",
      published: "Veröffentlicht",
      updated: "Aktualisiert",
      readTime: "Min. Lesezeit",
      disclaimerTitle: "Hinweis zu medizinischen Informationen",
      disclaimer: "Dieser Inhalt dient nur der allgemeinen Information und ist keine medizinische Beratung, Diagnose oder Behandlungsempfehlung. Alle Diagnosen und klinischen Entscheidungen müssen von einem lizenzierten Zahnarzt nach einer persönlichen Untersuchung getroffen werden.",
      indicativePrice: "Nur Richtpreis. Die endgültigen Kosten hängen von der klinischen Beurteilung, Diagnostik, den Materialien und der Komplexität ab.",
      pricePlaceholder: "Preis auf Anfrage",
      notFoundTitle: "Seite nicht gefunden",
      notFoundBody: "Die gesuchte Seite existiert nicht oder wurde verschoben.",
      backHome: "Zurück zur Startseite",
      whatToCheck: "Was Sie vor der Wahl prüfen sollten",
    },
  },
};
